import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Paper, Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, MenuItem,
  Alert, Tooltip, IconButton, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GavelIcon from '@mui/icons-material/Gavel';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import axios from '../../api/client';
import { AuthContext } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';

// The deposits register: what is expected, what is held, and what has been settled.
//
// This is also the subledger for 1200 Tenant Deposits Held. The total of agency-held,
// unsettled deposits should equal the balance on that account, and until this screen existed
// that check could only be run against the database by hand.
//
// SETTLEMENT IS NOT MOVE-OUT. Settling assesses the money and nothing else: the unit is not
// released and the tenancy is not ended.
const STATUS_CHIP = {
  pending: { label: 'Expected', color: 'default' },
  held: { label: 'Held', color: 'success' },
  settled: { label: 'Settled', color: 'info' }
};

const SecurityDeposits = () => {
  const { can } = useContext(AuthContext);
  const { formatMoney } = useCurrency();
  // Granted on the roles screen rather than tied to being an admin, so an agency can give
  // settlement to whoever actually does it.
  const canSettle = can('payments', 'settle');

  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [settling, setSettling] = useState(null);
  const [deductions, setDeductions] = useState([]);
  const [refundFromAccountId, setRefundFromAccountId] = useState('');
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      const res = await axios.get('/security-deposits');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load security deposits');
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get('/accounting/chart-of-accounts');
        const all = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setAccounts(all.filter((a) => a.type === 'asset'));
      } catch (e) { /* the list is a convenience; the server falls back on its own */ }
    };
    load();
  }, []);

  // Recalculated on the server so the figures on screen are the ones it will actually use,
  // rather than a second implementation of the same arithmetic that can drift from it.
  const refreshPreview = useCallback(async (depositId, lines) => {
    try {
      const res = await axios.post(`/security-deposits/${depositId}/settlement-preview`, {
        deductions: lines.filter((l) => l.description.trim() && parseFloat(l.amount) > 0)
          .map((l) => ({ description: l.description.trim(), amount: parseFloat(l.amount) }))
      });
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
      toast.error(e.response?.data?.message || 'Could not work out the settlement');
    }
  }, []);

  const openSettle = async (row) => {
    setSettling(row);
    setDeductions([]);
    setNotes('');
    setRefundFromAccountId('');
    await refreshPreview(row._id, []);
  };

  const setLine = (index, field, value) => {
    const next = deductions.map((l, i) => (i === index ? { ...l, [field]: value } : l));
    setDeductions(next);
  };

  const handleSettle = async () => {
    const lines = deductions
      .filter((l) => l.description.trim() || l.amount)
      .map((l) => ({ description: l.description.trim(), amount: parseFloat(l.amount) }));

    if (lines.some((l) => !l.description)) return toast.error('Every deduction needs a description');
    if (lines.some((l) => !(l.amount > 0))) return toast.error('Every deduction needs a positive amount');

    const confirmed = window.confirm(
      `Settle this deposit of ${formatMoney(settling.amount)}?\n\n`
      + (preview ? `Refund: ${formatMoney(preview.refundAmount)}   Still owed: ${formatMoney(preview.shortfallAmount)}\n\n` : '')
      + 'This raises the damage as an invoice, spends the deposit against what the tenant owes, '
      + 'and pays back whatever is left. It does not release the unit.'
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const { data } = await axios.post(`/security-deposits/${settling._id}/settle`, {
        deductions: lines,
        refundFromAccountId: refundFromAccountId || undefined,
        notes: notes || undefined
      });
      toast.success(data.message);
      setSettling(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not settle this deposit');
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async (row) => {
    if (!window.confirm(`Withdraw the expected deposit of ${formatMoney(row.amount)}? Nothing has been received against it.`)) return;
    try {
      const { data } = await axios.delete(`/security-deposits/${row._id}`);
      toast.success(data.message);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not withdraw this deposit');
    }
  };

  // The control this screen exists for: agency-held and unsettled should equal 1200.
  const heldByAgency = rows
    .filter((r) => r.status === 'held' && r.heldBy === 'agency')
    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
  const heldByLandlords = rows
    .filter((r) => r.status === 'held' && r.heldBy === 'landlord')
    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);

  return (
    <Paper sx={{ p: 3 }}>
      <Box mb={2}>
        <Typography variant="h5">Security Deposits</Typography>
        <Typography variant="body2" color="textSecondary">
          One month&rsquo;s rent, held against damage and arrears. Settling assesses the money;
          it does not release the unit.
        </Typography>
      </Box>

      <Box display="flex" gap={3} mb={2} flexWrap="wrap">
        <Typography variant="body2">
          <strong>{formatMoney(heldByAgency)}</strong> held by the agency
          <Typography variant="caption" display="block" color="textSecondary">
            should equal account 1200
          </Typography>
        </Typography>
        <Typography variant="body2">
          <strong>{formatMoney(heldByLandlords)}</strong> held by landlords
          <Typography variant="caption" display="block" color="textSecondary">
            sits on each landlord&rsquo;s own account
          </Typography>
        </Typography>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Tenant</TableCell>
            <TableCell>Property</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Held by</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Received</TableCell>
            <TableCell align="right">Refunded</TableCell>
            <TableCell align="right">Still owed</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row._id} hover>
              <TableCell>{row.tenant?.name || '—'}</TableCell>
              <TableCell>
                {row.property?.propertyName || '—'}
                {row.unit?.unitNumber ? ` · Unit ${row.unit.unitNumber}` : ''}
              </TableCell>
              <TableCell align="right">{formatMoney(row.amount)}</TableCell>
              <TableCell>{row.heldBy ? (row.heldBy === 'agency' ? 'Agency' : 'Landlord') : '—'}</TableCell>
              <TableCell>
                <Chip size="small" variant="outlined"
                  label={STATUS_CHIP[row.status]?.label || row.status}
                  color={STATUS_CHIP[row.status]?.color || 'default'} />
              </TableCell>
              <TableCell>{row.receivedAt ? format(new Date(row.receivedAt), 'dd MMM yy') : '—'}</TableCell>
              <TableCell align="right">{row.status === 'settled' ? formatMoney(row.refundAmount) : '—'}</TableCell>
              <TableCell align="right">
                {row.status === 'settled' && parseFloat(row.shortfallAmount) > 0
                  ? formatMoney(row.shortfallAmount)
                  : (row.status === 'settled' ? '—' : '')}
              </TableCell>
              <TableCell align="right">
                {row.status === 'held' && canSettle && (
                  <Tooltip title="Settle this deposit">
                    <IconButton size="small" onClick={() => openSettle(row)}><GavelIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
                {row.status === 'pending' && can('payments', 'delete') && (
                  <Tooltip title="Withdraw this expected deposit">
                    <IconButton size="small" onClick={() => handleWithdraw(row)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  No deposits recorded. One is created when a security payment is received.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={!!settling} onClose={() => !saving && setSettling(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Settle Security Deposit</DialogTitle>
        <DialogContent dividers>
          {settling && (
            <>
              <Typography variant="body2" gutterBottom>
                {settling.tenant?.name} · {formatMoney(settling.amount)} held by{' '}
                {settling.heldBy === 'agency' ? 'the agency' : 'the landlord'}
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 2 }}>Deductions</Typography>
              <Typography variant="caption" color="textSecondary">
                Each becomes a line on an invoice the tenant can be shown. Leave empty if there is no damage.
              </Typography>

              {deductions.map((line, i) => (
                <Grid container spacing={1} key={i} sx={{ mt: 0.5 }}>
                  <Grid item xs={8}>
                    <TextField fullWidth size="small" label="What" value={line.description}
                      onChange={(e) => setLine(i, 'description', e.target.value)} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" type="number" label="Amount" value={line.amount}
                      onChange={(e) => setLine(i, 'amount', e.target.value)}
                      onBlur={() => refreshPreview(settling._id, deductions)} />
                  </Grid>
                </Grid>
              ))}

              <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }}
                onClick={() => setDeductions([...deductions, { description: '', amount: '' }])}>
                Add a deduction
              </Button>

              <Divider sx={{ my: 2 }} />

              {preview && (
                <Box>
                  <Typography variant="body2">Owed now: {formatMoney(preview.arrears)}</Typography>
                  <Typography variant="body2">Damage: {formatMoney(preview.deductionsTotal)}</Typography>
                  <Typography variant="body2">
                    <strong>Refund to tenant: {formatMoney(preview.refundAmount)}</strong>
                  </Typography>
                  {preview.shortfallAmount > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {formatMoney(preview.shortfallAmount)} will remain owed by the tenant. The rent is
                      settled first, so what is left short is the damage, which the landlord carries
                      until the tenant pays it.
                    </Alert>
                  )}
                </Box>
              )}

              {preview?.refundAmount > 0 && (
                <TextField select fullWidth size="small" sx={{ mt: 2 }}
                  label="Refund paid out of"
                  value={refundFromAccountId}
                  onChange={(e) => setRefundFromAccountId(e.target.value)}
                  helperText={settling.heldBy === 'landlord'
                    ? "The landlord's account is debited for the deposit, so the refund comes out of their money."
                    : 'Cleared from Tenant Deposits Held.'}
                >
                  <MenuItem value="">Default cash account</MenuItem>
                  {accounts.map((a) => (
                    <MenuItem key={a._id} value={a._id}>{a.code} {a.name}</MenuItem>
                  ))}
                </TextField>
              )}

              <TextField fullWidth multiline minRows={2} sx={{ mt: 2 }} label="Notes"
                value={notes} onChange={(e) => setNotes(e.target.value)} />

              <Alert severity="info" sx={{ mt: 2 }}>
                This does not release the unit or end the tenancy.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettling(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSettle} disabled={saving}>
            {saving ? 'Settling…' : 'Settle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SecurityDeposits;
